import { useCallback } from 'react'
import { type Core } from '@pdftron/webviewer'

type ScrollSource = 'original' | 'revised'

interface PageMatch {
  originalPageIndex: number
  revisedPageIndex: number
}

interface UseDualScrollParams {
  jobManifestMatches: PageMatch[] | undefined
}

/**
 * Returns a `setupDualScroll` function that wires up synchronized scrolling
 * between two WebViewer document containers. Call it after the MULTI_VIEWER_READY
 * event with the two containers and viewers. Returns a cleanup function.
 */
export function useDualScroll({ jobManifestMatches }: UseDualScrollParams) {
  const setupDualScroll = useCallback(
    (
      originalViewer: Core.DocumentViewer,
      revisedViewer: Core.DocumentViewer,
      originalContainer: Element | null,
      revisedContainer: Element | null,
      toggleButtonId: string
    ): (() => void) => {
      let isDualScroll = true
      let lastScrollTime = 0
      let lastScrollPosition = 0
      let sideHovered: ScrollSource = 'revised'
      const speedThreshold = 2000
      const debounceDelay = 250
      let isFastScroll = false
      let prevPage = 1
      let scrollTimeoutId: ReturnType<typeof setTimeout> | null = null

      const toggleButton = document.getElementById(toggleButtonId)
      const handleToggle = () => {
        isDualScroll = !isDualScroll
      }
      toggleButton?.addEventListener('click', handleToggle)

      const executeScrollPageMapping = (source: ScrollSource) => {
        if (!jobManifestMatches) return
        if (source !== sideHovered) return

        const isOriginal = source === 'original'
        const currentPage = isOriginal
          ? originalViewer.getCurrentPage()
          : revisedViewer.getCurrentPage()

        if (currentPage !== prevPage) {
          const targetViewer = isOriginal ? revisedViewer : originalViewer

          const matchPage = isOriginal
            ? jobManifestMatches.find((match) => match.originalPageIndex === currentPage)
                ?.revisedPageIndex
            : jobManifestMatches.find((match) => match.revisedPageIndex === currentPage)
                ?.originalPageIndex

          if (matchPage != null) {
            targetViewer.setCurrentPage(matchPage, true)
          }

          prevPage = currentPage
        }
      }

      const handleDualVerticalScroll = (source: ScrollSource) => {
        if (!isDualScroll) return

        const now = performance.now()
        const container = source === 'original' ? originalContainer : revisedContainer
        const scrollPosition = container?.scrollTop ?? 0
        const scrollSpeed =
          (Math.abs(scrollPosition - lastScrollPosition) / (now - lastScrollTime)) * 1000

        lastScrollTime = now
        lastScrollPosition = scrollPosition
        isFastScroll = scrollSpeed > speedThreshold

        if (scrollTimeoutId !== null) clearTimeout(scrollTimeoutId)
        scrollTimeoutId = setTimeout(() => {
          executeScrollPageMapping(source)
          isFastScroll = false
        }, debounceDelay)

        if (isFastScroll) {
          setTimeout(() => {
            if (!isFastScroll) {
              executeScrollPageMapping(source)
            }
          }, debounceDelay)
        }
      }

      const handleScrollOriginal = () => handleDualVerticalScroll('original')
      const handleScrollRevised = () => handleDualVerticalScroll('revised')
      const handleMouseEnterOriginal = () => {
        sideHovered = 'original'
      }
      const handleMouseLeaveOriginal = () => {
        sideHovered = 'revised'
      }

      originalContainer?.addEventListener('scroll', handleScrollOriginal)
      revisedContainer?.addEventListener('scroll', handleScrollRevised)
      originalContainer?.addEventListener('mouseenter', handleMouseEnterOriginal)
      originalContainer?.addEventListener('mouseleave', handleMouseLeaveOriginal)

      return () => {
        originalContainer?.removeEventListener('scroll', handleScrollOriginal)
        revisedContainer?.removeEventListener('scroll', handleScrollRevised)
        originalContainer?.removeEventListener('mouseenter', handleMouseEnterOriginal)
        originalContainer?.removeEventListener('mouseleave', handleMouseLeaveOriginal)
        toggleButton?.removeEventListener('click', handleToggle)
        if (scrollTimeoutId !== null) clearTimeout(scrollTimeoutId)
      }
    },
    [jobManifestMatches]
  )

  return { setupDualScroll }
}
